/**
 * 감자단 잡담 게시판(`/community/talk`) 공용 상수·타입 (JCG-3029).
 *
 * 클라이언트/서버 양쪽에서 import 하는 순수 모듈. DB CHECK 제약
 * (`supabase/migrations/20260918090000_community_talk_board.sql`)과 길이 제한을 일치시킨다.
 */

export const COMMUNITY_TALK_PATH = "/community/talk";
export const COMMUNITY_TALK_LABEL = "감자단 잡담";

/** 목록 탭: 전체글 / 추천글(추천 임계값 이상). */
export type TalkListTab = "all" | "hot";

/** 목록 정렬: 최신순 / 추천순(추천 desc → 최신). */
export type TalkSort = "latest" | "recommend";

export const TALK_LIMITS = {
  title: 80,
  content: 5000,
  /** 작성자 표시 이름(Clerk 이름을 자른다) */
  nickname: 20,
  /** 본문 인라인 이미지(BlockEditor) 최대 장수 */
  images: 3,
} as const;

export const TALK_PAGE_SIZE = 30;

/** 추천글 기준 추천 수. 서비스 규모가 작아 낮게 시작한다. */
export const TALK_HOT_THRESHOLD = 3;

export function isTalkListTab(value: unknown): value is TalkListTab {
  return value === "all" || value === "hot";
}

export function isTalkSort(value: unknown): value is TalkSort {
  return value === "latest" || value === "recommend";
}

/** 댓글 slug — `getCommentPagePath` 기본 규칙(`/${slug}`)이 실제 라우트와 일치하도록 경로를 그대로 쓴다. */
export function talkCommentSlug(postId: number): string {
  return `community/talk/${postId}`;
}

/** 상세 metadata "use cache" 태그. */
export function talkPostCacheTag(postId: number): string {
  return `community-talk-${postId}`;
}

export function talkPostHref(postId: number): string {
  return `${COMMUNITY_TALK_PATH}/${postId}`;
}

export function newTalkPostHref(): string {
  return `${COMMUNITY_TALK_PATH}/new`;
}

export function talkListHref(params?: {
  tab?: TalkListTab;
  sort?: TalkSort;
  page?: number;
}): string {
  const search = new URLSearchParams();
  if (params?.tab && params.tab !== "all") search.set("tab", params.tab);
  if (params?.sort && params.sort !== "latest") search.set("sort", params.sort);
  if (params?.page && params.page > 1) search.set("page", String(params.page));
  const query = search.toString();
  return `${COMMUNITY_TALK_PATH}${query ? `?${query}` : ""}`;
}

/** 내 글 보기(로그인 필요, 클라이언트가 server action 으로 조회). */
export const MY_TALK_POSTS_HREF = `${COMMUNITY_TALK_PATH}?mine=1`;

export interface TalkPost {
  id: number;
  clerkUserId: string;
  author: string;
  profileImageUrl: string | null;
  passType: string | null;
  title: string;
  content: string;
  imageUrls: string[];
  viewCount: number;
  recommendCount: number;
  /** 목록 조회 시에만 채워지는 댓글 수(삭제 댓글 제외). */
  commentCount?: number;
  /** 관리자 공지 — 목록 맨 위 고정, 일반 목록·추천글 TOP 에서는 제외. */
  isNotice: boolean;
  contentEditedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 목록 맨 위에 고정하는 공지 최대 수(최신순). */
export const TALK_NOTICE_LIMIT = 5;

export function isHotTalkPost(post: Pick<TalkPost, "recommendCount">): boolean {
  return post.recommendCount >= TALK_HOT_THRESHOLD;
}

/**
 * 표 목록용 날짜: 오늘(KST)이면 "HH:mm", 아니면 "MM.DD". 디시인사이드 목록 관례.
 * `now` 를 넘기면 결정적으로 계산할 수 있다(테스트·서버/클라이언트 일치).
 */
export function formatTalkListDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const dayOf = (d: Date) =>
    d.toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" });
  if (dayOf(date) === dayOf(now)) {
    return date.toLocaleTimeString("ko-KR", { timeZone: "Asia/Seoul", hour: "2-digit", minute: "2-digit", hour12: false });
  }
  const parts = new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", month: "2-digit", day: "2-digit" })
    .formatToParts(date)
    .filter((p) => p.type === "month" || p.type === "day")
    .map((p) => p.value);
  return parts.join(".");
}

/** 상세 페이지용 전체 시각(KST). */
export function formatTalkDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
